<template>
  <TableList
    v-rect-loading="loading"
    class="source-code-page"
    row-key="id"
    :title="$t(`${i18nPrefix}.tab.sourceCodeManagement`)"
    :listData="sourceCodeList"
    :tableColumns="tableColumns"
    :pagination="pagination"
    :actionBtn="actionBtn"
    @create="handelCreateSourceCode"
  >
    <template #filter>
      <div class="header-filter">
        <span class="company-select">
          <el-select
            v-model="selectedCompanyId"
            filterable
            clearable
            ref="companySelect"
            :loading="company.loading"
            :placeholder="$t(`${i18nPrefix}.label.defaultChannel`)"
            :no-data-text="$t(`${i18nPrefix}.label.pleaseStartTyping`)"
            :filter-method="filterCompany"
            @change="handelSelectCompany"
            @clear="handleSelectClear"
            @visible-change="handleVisibleChange"
          >
            <el-option
              v-for="item in company.showData"
              :key="item.id"
              :label="item.label"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <span class="status-select">
          <el-select v-model="selectedStatus" @change="handleStatusSelect">
            <el-option
              v-for="item in statusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <el-input
          v-model="keyword"
          class="search-input"
          clearable
          :title="$t(`${i18nPrefix}.placeholder.search`)"
          :placeholder="$t(`${i18nPrefix}.placeholder.search`)"
          @input="handleKeywordsChange"
        >
        </el-input>
      </div>
    </template>
    <template #selectableHeader="{ column}">
      <div v-auto-tooltip.always="{ content: $t(`${i18nPrefix}.tip.selectableTip`) }" class="selectable-header">
        <span class="label">{{ column.label }}</span>
        <i class="tip iconfont iconquestion-mark"></i>
      </div>
    </template>
    <template #selectable="{ data, row }">
      <el-switch :value="!data" @change="onStatusChange($event, row)"></el-switch>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { mapActions, mapGetters } from 'vuex'
import { addI18nProperty } from '@/i18n'

export default {
  components: {
    TableList
  },
  props: {

  },
  data () {
    return {
      i18nPrefix: 'admin.customer.sourceChannelManagement',
      loading: false,
      allSourceCodeList: [],
      companyList: [],
      selectedCompanyId: null,
      tempCompanyId: null,
      selectedStatus: 0,
      keyword: null,
      keywordsSearchDebounce: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      actionBtn: {
        edit: {
          show: true,
          onclick: this.handleEditSourceCode
        }
      },
      company: {
        data: null,
        loading: false,
        filterData: null,
        showData: null,
        hasLoad: false,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    account () {
      return this.getCurrentAccount()
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.source`),
          dataKey: 'source',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.code`),
          dataKey: 'code',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'channel.id',
          formatter: (data) => {
            return this.$t(`${this.i18nPrefix}.label.${data ? 'classified' : 'unclassified'}`)
          },
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.channel`),
          dataKey: 'channel.i18nName',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.selectable`),
          dataKey: 'hide_on_page',
          headerSlotName: 'selectableHeader',
          slotName: 'selectable'
        }
      ]
    },
    statusList () {
      return [
        { value: 0, label: this.$t(`${this.i18nPrefix}.label.all`) },
        { value: 1, label: this.$t(`${this.i18nPrefix}.label.classified`) },
        { value: 2, label: this.$t(`${this.i18nPrefix}.label.unclassified`) }
      ]
    },
    queryCompany () {
      return this.$route.query.company
    },
    queryKeyword () {
      return this.$route.query.keyword
    },
    queryStatus () {
      return this.$route.query.status
    },
    sourceCodeList () {
      const sourceCodeList = [...this.allSourceCodeList]
      const startIndex = (this.pagination.page - 1) * this.pagination.pageSize
      return sourceCodeList.splice(startIndex, this.pagination.pageSize)
    }
  },
  created () {
    if (!this.company.hasLoad) {
      this.loadCompanyList()
    }
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('superAdmin', ['getSuperAdminCompanyList']),
    ...mapActions('sourceCode', ['getSourceCodeList', 'updateSourceCodeById']),
    async loadCompanyList () {
      if (this.company.loading) {
        return
      }
      this.loading = true
      this.company.loading = true
      try {
        const params = {
          status: 4
        }
        const resp = await this.getSuperAdminCompanyList(params)
        if (resp && resp.result) {
          const rootCompanyList = resp.result
          _.forEach(rootCompanyList, company => {
            addI18nProperty(company, 'companyName', 'cn_name', 'name')
            const companyName = company.companyName
            const label = `${company.id} - ${companyName ? `${companyName}` : ''}`
            this.$set(company, 'label', label)
          })
          this.company.data = rootCompanyList
          this.company.hasLoad = true
        }
      } finally {
        this.loading = false
        this.company.loading = false
      }
    },
    async loadSourceCodeList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      const params = {
        classify: this.selectedStatus,
        company_id: this.selectedCompanyId || -1,
        keyword: this.keyword,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      this.loading = true
      try {
        const resp = await this.getSourceCodeList(params)
        if (resp) {
          const vm = this
          this.allSourceCodeList = _.chain(resp).orderBy(['id'], 'desc')
            .forEach(item => {
              if (!item.channel) {
                item.channel = {
                  id: item.classify ? -1 : null,
                  get i18nName () {
                    return vm.$t(`${vm.i18nPrefix}.label.weChatChannel`)
                  }
                }
              } else {
                addI18nProperty(item.channel, 'i18nName', 'cn_name', 'name')
              }
            }).value()
          this.pagination.total = this.allSourceCodeList.length
        }
      } finally {
        this.loading = false
      }
    },
    async filterCompany (keyword) {
      const company = this.company
      if (!_.trim(keyword) || company.loading) {
        return
      }
      company.loading = true
      company.page = 1
      const filterData = _.filter(company.data, company => {
        return _.includes(_.toUpper(company.label), _.toUpper(keyword))
      })
      company.filterData = filterData
      company.showData = filterData.slice(0, company.pageSize)
      const elSelect = this.$refs.companySelect
      this.$nextTick(() => {
        const el = elSelect?.popperElm.querySelector('.el-select-dropdown__wrap')
        if (el && !el.hasAttribute('filter-scroll-listened')) {
          el.setAttribute('filter-scroll-listened', true)
          el.addEventListener('scroll', () => {
            const company = this.company
            const condition = el.scrollHeight - el.scrollTop - 20 <= el.clientHeight
            if (condition) {
              if (company.filterData && company.page * company.pageSize > company.filterData.length) {
                return
              }
              const data = company.filterData.slice(company.page * company.pageSize, (company.page + 1) * company.pageSize)
              company.showData.push(...data)
              company.page += 1
            }
          })
        }
      })
      company.loading = false
    },
    handelSelectCompany () {
      this.$router.push({ query: { ...this.$route.query, company: this.selectedCompanyId } })
    },
    handleSelectClear () {
      this.$router.push({ query: { ...this.$route.query, company: -1 } })
    },
    handleVisibleChange () {
      this.company.showData = []
    },
    handleEditSourceCode (row) {
      this.$router.push({ path: `/v-admin/client-admin/source-channel/management/code/edit/${row.id}`, query: { ...this.$route.query, company: this.selectedCompanyId || -1, keyword: this.keyword, status: this.selectedStatus } })
    },
    async onStatusChange (status, row) {
      this.loading = true
      try {
        const data = {
          sourceCodeId: row.id,
          data: {
            company_id: this.selectedCompanyId,
            hide_on_page: !status
          }
        }
        const resp = await this.updateSourceCodeById(data)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          row.hide_on_page = !status
        }
      } finally {
        this.loading = false
      }
    },
    handelCreateSourceCode () {
      this.$router.push({ path: '/v-admin/client-admin/source-channel/management/code/add', query: { ...this.$route.query, company: this.selectedCompanyId || -1 } })
    },
    handleStatusSelect () {
      if (parseInt(this.queryStatus) !== this.status) {
        this.$router.push({ query: { ...this.$route.query, status: this.selectedStatus } })
      }
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 500)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.$router.push({ query: { ...this.$route.query, keyword: this.keyword } })
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        let companyId = this.queryCompany
        this.keyword = this.queryKeyword
        const selectedStatus = parseInt(this.queryStatus)
        this.selectedStatus = _.isNaN(selectedStatus) ? 0 : selectedStatus
        if (!companyId || companyId === '-1') {
          this.selectedCompanyId = null
          this.loadSourceCodeList(true)
          return
        }
        if (!this.company.hasLoad) {
          await this.loadCompanyList()
        }
        companyId = parseInt(companyId)
        if (this.selectedCompanyId !== companyId) {
          this.selectedCompanyId = companyId
          const company = _.find(this.company.data, { id: this.selectedCompanyId })
          this.company.showData = [company]
        }
        this.loadSourceCodeList(true)
      }
    }
  }

}
</script>

<style lang="scss" scoped>
.source-code-page {
  padding: 0;

  .header-filter {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 10px;

    .text {
      margin-right: 10px;
      display: inline-block;
      vertical-align: middle;
    }

    .company-select {
      display: inline-block;
      vertical-align: middle;
    }

    .status-select {
      display: inline-block;
      vertical-align: middle;
    }

    ::v-deep {
      .el-input__inner {
        border: 1px solid #c0c4cc;
      }

      .el-input-group__append {
        border-left: none;
        padding: 0 5px;
        background-color: #fff;
      }
    }

    .icon-search {
      cursor: pointer;
      font-size: 18px;
    }
  }

  .selectable-header {
    display: inline;
    cursor: help;

    .tip {
      position: relative;
      top: -2px;
      font-size: 16px;
    }
  }

  ::v-deep {
    .list-head {
      height: auto;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-bottom: 10px;

      .title {
        margin-bottom: 10px;
      }

      .btn-container {
        display: flex;
        margin-left: 10px;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
